/** Route effect key → the persisted rule/action `type` (route_to_queue collapses to 'route').
 *  Exported so the multi-action route compiler (`route-multi.ts`) shares one source of truth. */
export function ruleTypeFor(effectKey) {
    return (effectKey === 'route_to_queue' ? 'route' : effectKey);
}
/** moment → (trigger, scope, default stage). P3b-3 (gaps #4/#6): the two
 *  balance-named moments were FREED to mean their like-named triggers, and the
 *  export moment moved onto `pre_export` (the P2 `batch_complete → on-export`
 *  mapping is retired). `scheduled` carries the form-scope `on-schedule` trigger. */
export const MOMENT = {
    step_enter: { trigger: 'on-step-enter', scope: 'form', stage: 'verification' },
    // R2 (E-062) — "accepted at step": evaluated at the accept advance
    // (advanceDocument, trigger 'post_edit'), filtered by the ACCEPTED step's
    // stage against the rule's stages[]. Only `notify` declares this moment in
    // the registry today. NOTE: batch_classified/batch_complete stay in this
    // table for PARSE compatibility with stored rules, but no route effect
    // declares them anymore (R0 — no action eval site exists).
    on_validate: { trigger: 'on-validate', scope: 'form', stage: 'verification' },
    pre_export: { trigger: 'on-export', scope: 'batch', stage: 'export' },
    batch_classified: { trigger: 'on-batch-classified', scope: 'batch', stage: 'verification' },
    batch_complete: { trigger: 'on-batch-complete', scope: 'batch', stage: 'verification' },
    scheduled: { trigger: 'on-schedule', scope: 'form', stage: 'verification' },
};
export function momentForTrigger(trigger) {
    switch (trigger) {
        case 'on-validate': return 'on_validate'; // R2 — accepted at step
        case 'on-export': return 'pre_export';
        case 'on-batch-classified': return 'batch_classified';
        case 'on-batch-complete': return 'batch_complete';
        case 'on-schedule': return 'scheduled';
        default: return 'step_enter';
    }
}
/**
 * Factory for the seven Route effects. compile builds a CreateRuleAttachmentInput
 * carrying a SINGLE action (one step = one rule); the rule's name/stage/severity +
 * the action's params ride step.config; the IF is first-class (step.condition).
 * parse recovers them. Rule/action ids are deterministic placeholders — the editor/
 * store assign real ids on save.
 */
export function makeRouteEffect(effectKey) {
    const ruleType = ruleTypeFor(effectKey);
    const compile = (step) => {
        const m = MOMENT[step.moment] ?? MOMENT.step_enter;
        const { name = '', stage, severity = 'error', overrideBehavior, overrideConfig, ...actionParams } = step.config;
        return {
            name,
            type: ruleType,
            scope: m.scope,
            trigger: m.trigger,
            stage: stage ?? m.stage,
            severity,
            overrideBehavior,
            overrideConfig,
            config: {},
            condition: step.condition,
            actions: [{ id: `${effectKey}-0`, type: ruleType, config: actionParams, displayOrder: 0 }],
        };
    };
    const parse = (payload) => {
        if (!payload || typeof payload !== 'object')
            return null;
        const rule = payload;
        const action = rule.actions?.[0];
        if (!action || action.type !== ruleType)
            return null;
        return {
            effectKey,
            moment: momentForTrigger(String(rule.trigger)),
            scope: rule.scope ?? 'batch',
            condition: rule.condition,
            config: {
                name: rule.name ?? '',
                stage: rule.stage,
                severity: rule.severity,
                overrideBehavior: rule.overrideBehavior,
                overrideConfig: rule.overrideConfig,
                ...(action.config ?? {}),
            },
        };
    };
    return { compile, parse };
}
//# sourceMappingURL=route-effect.js.map